import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { deleteConversation, fetchConversationDeletion } from './atlasApi';
import '../files/FileBrowser.css';
import './DeleteChatDialog.css';

export const NOT_STARTER_MESSAGE = 'Only the person who started this chat can delete it.';
const NOT_ALLOWED_MESSAGE = "You can't delete this chat.";
// A 404 preview: already deleted, or not a chat this person can see. The
// server doesn't say which, so neither do we, and the row stays put.
export const NOT_FOUND_MESSAGE = "This chat can't be deleted. It may already have been deleted.";

function countOf(counts, key) {
  const n = Number(counts?.[key]);
  return Number.isFinite(n) && n > 0 ? Math.floor(n) : 0;
}

function plural(n, one, many = `${one}s`) {
  return `${n} ${n === 1 ? one : many}`;
}

/**
 * The "what goes" list, one line per kind of thing the chat holds; kinds it
 * has none of are left out. With keepFiles the files are not deleted, so
 * their line drops out too. counts may be null (a teammate's preview of a
 * company chat they didn't start): no lines.
 */
export function deletionSummaryLines(counts, { keepFiles = false } = {}) {
  const lines = [];
  if (countOf(counts, 'messages')) lines.push('All messages in this chat');
  const briefs = countOf(counts, 'briefs');
  if (briefs) {
    const shared = countOf(counts, 'published_briefs');
    lines.push(shared
      ? `${plural(briefs, 'brief')} (${plural(shared, 'shared link')} will stop working)`
      : plural(briefs, 'brief'));
  }
  const charts = countOf(counts, 'charts');
  if (charts) lines.push(plural(charts, 'chart'));
  const images = countOf(counts, 'images');
  if (images) lines.push(plural(images, 'image'));
  const files = countOf(counts, 'files');
  if (files && !keepFiles) {
    const filed = countOf(counts, 'files_in_folders');
    lines.push(filed
      ? `${plural(files, 'file')} (${filed} filed in ${filed === 1 ? 'a folder' : 'folders'})`
      : plural(files, 'file'));
  }
  const memories = countOf(counts, 'memories');
  if (memories) lines.push(`${plural(memories, 'thing')} Atlas learned from this chat`);
  return lines;
}

function blockedText(reason) {
  return reason === 'not_starter' ? NOT_STARTER_MESSAGE : NOT_ALLOWED_MESSAGE;
}

/** A server-written sentence when there is one; never a bare code or "failed: 500". */
function errorText(err, fallback) {
  const detail = typeof err?.body?.detail === 'string' ? err.body.detail.trim() : '';
  return detail || fallback;
}

/**
 * Confirm and run "Delete chat". Loads what the chat holds first (the server
 * also says whether this user may delete it), then deletes on confirm.
 *   conversation: { conversation_id, title } from the chat list
 *   onDeleted(conversationId, { filesKept, alreadyDeleted }): it is gone (a 404 on delete counts)
 *   onCancel(): close without deleting
 */
export default function DeleteChatDialog({ conversation, onCancel, onDeleted }) {
  const conversationId = conversation?.conversation_id || null;
  const [loadState, setLoadState] = useState('loading'); // loading | ready | error
  const [preview, setPreview] = useState(null);
  const [loadError, setLoadError] = useState('');
  const [keepFiles, setKeepFiles] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState('');
  // Set when the delete itself is refused (403), e.g. the preview was stale.
  const [refusedReason, setRefusedReason] = useState(null);
  const cancelRef = useRef(null);
  const mountedRef = useRef(false);
  const onDeletedRef = useRef(onDeleted);
  onDeletedRef.current = onDeleted;

  useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; };
  }, []);

  useEffect(() => { cancelRef.current?.focus(); }, []);

  useEffect(() => {
    let cancelled = false;
    setLoadState('loading');
    setPreview(null);
    setLoadError('');
    setKeepFiles(false);
    setDeleteError('');
    setRefusedReason(null);
    fetchConversationDeletion(conversationId)
      .then((data) => {
        if (cancelled) return;
        setPreview(data || {});
        setLoadState('ready');
      })
      .catch((err) => {
        if (cancelled) return;
        setLoadError(err?.status === 404
          ? NOT_FOUND_MESSAGE
          : errorText(err, "Couldn't load this chat's details. Please try again."));
        setLoadState('error');
      });
    return () => { cancelled = true; };
  }, [conversationId]);

  const close = useCallback(() => {
    if (!deleting) onCancel?.();
  }, [deleting, onCancel]);

  useEffect(() => {
    const onKeyDown = (event) => {
      if (event.key === 'Escape') {
        event.stopPropagation();
        close();
      }
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [close]);

  const counts = preview?.counts || {};
  const fileCount = countOf(counts, 'files');
  const isCompanyChat = preview?.scope_type === 'company';
  const blockedReason = loadState === 'ready'
    ? (preview?.can_delete === false ? (preview?.reason || 'not_allowed') : refusedReason)
    : null;
  const canDelete = loadState === 'ready' && !blockedReason;
  const keepFilesChecked = fileCount > 0 && keepFiles;
  const lines = deletionSummaryLines(counts, { keepFiles: keepFilesChecked });
  // Once the preview is in, its title wins: the server sends null to a
  // teammate who didn't start the chat, and then it is just "this chat".
  const title = String((preview ? preview.title : conversation?.title) || '').trim();
  const heading = title ? `Delete "${title}"?` : 'Delete this chat?';

  const handleDelete = async () => {
    if (!canDelete || deleting) return;
    setDeleting(true);
    setDeleteError('');
    try {
      const result = await deleteConversation({ conversationId, keepFiles: keepFilesChecked });
      const filesKept = Number(result?.files_kept);
      onDeletedRef.current?.(conversationId, {
        filesKept: Number.isFinite(filesKept) ? filesKept : 0,
        alreadyDeleted: false,
      });
    } catch (err) {
      if (err?.status === 404) {
        onDeletedRef.current?.(conversationId, { filesKept: 0, alreadyDeleted: true });
        return;
      }
      if (!mountedRef.current) return;
      if (err?.status === 403) {
        setRefusedReason(err?.code === 'not_starter' ? 'not_starter' : 'not_allowed');
      } else {
        setDeleteError(errorText(err, "Couldn't delete this chat. Please try again."));
      }
      setDeleting(false);
    }
  };

  return createPortal(
    <div className="fl-layer delete-chat-layer" onClick={close}>
      <div
        className="fl-dialog delete-chat-dialog"
        role="alertdialog"
        aria-modal="true"
        aria-labelledby="delete-chat-title"
        aria-describedby="delete-chat-body"
        aria-busy={loadState === 'loading' || deleting ? 'true' : undefined}
        onClick={(e) => e.stopPropagation()}
      >
        <h3 id="delete-chat-title" className="fl-dialog-title">{heading}</h3>
        <div id="delete-chat-body" className="delete-chat-body">
          {loadState === 'loading' ? (
            <div className="delete-chat-loading" role="status">
              <span className="fl-spinner" aria-hidden="true" />
              <span>Checking what this chat holds…</span>
            </div>
          ) : null}
          {loadState === 'error' ? <p className="delete-chat-error" role="alert">{loadError}</p> : null}
          {blockedReason ? <p className="fl-dialog-text" role="note">{blockedText(blockedReason)}</p> : null}
          {canDelete ? (
            <>
              {lines.length ? (
                <>
                  <p className="fl-dialog-text">This permanently deletes:</p>
                  <ul className="delete-chat-list" aria-label="What will be deleted">
                    {lines.map((line) => <li key={line}>{line}</li>)}
                  </ul>
                </>
              ) : (
                <p className="fl-dialog-text">This permanently deletes the chat.</p>
              )}
              {isCompanyChat ? (
                <p className="fl-dialog-text">This company chat will be removed for everyone in your company.</p>
              ) : null}
              {fileCount > 0 ? (
                <label className="delete-chat-check">
                  <input
                    type="checkbox"
                    checked={keepFiles}
                    disabled={deleting}
                    onChange={(e) => setKeepFiles(e.target.checked)}
                  />
                  <span>
                    {isCompanyChat
                      ? `Move these files to my personal library (${fileCount})`
                      : `Keep these files in my library (${fileCount})`}
                  </span>
                </label>
              ) : null}
              <p className="delete-chat-muted">This can't be undone.</p>
              {deleteError ? <p className="delete-chat-error" role="alert">{deleteError}</p> : null}
            </>
          ) : null}
        </div>
        <div className="fl-dialog-actions">
          <button type="button" ref={cancelRef} className="fl-btn" onClick={close} disabled={deleting}>
            Cancel
          </button>
          {canDelete ? (
            <button type="button" className="fl-btn fl-btn-danger" onClick={handleDelete} disabled={deleting}>
              {deleting ? <span className="fl-spinner" aria-hidden="true" /> : null}
              {deleting ? 'Deleting…' : 'Delete chat'}
            </button>
          ) : null}
        </div>
      </div>
    </div>,
    document.body,
  );
}
